/**
 * Tap Protection — Administrator Guide (English)
 *
 * Content is authored as a plain string of Markdown so it stays close
 * to `docs/TAP_PROTECTION_ADMIN_GUIDE.md`. Whenever that source
 * document is updated, this string should mirror it.
 */
const content = `# Tap Protection — Administrator Guide

**Where to find these settings**: Every card's **Settings** modal → *Tap Protection* section.
**Where to see the runtime**: **Super Admin → Tap Analytics Health Monitor** (\`/admin/tap-health\`).

---

## In 30 seconds

Tap Protection decides whether a customer's NFC tap gets counted or filtered out as suspicious.

* **Tap Protection ON** — the platform applies anti-fraud checks. Only "clean" taps count.
* **Tap Protection OFF** — every legitimate tap counts, no filtering. Choose this if you want raw analytics with no protection.

Underneath the master switch there are two independent sub-protections you can turn on or off:

| Sub-protection | Blocks | Applies to |
|---|---|---|
| **Duplicate Protection** | Repeat taps from the same phone within a short window | Every card |
| **Minimum Dwell Protection** | Taps where the customer didn't stay on the branded page long enough | Landing Page and Review Shield cards only |

---

## How to enable Tap Protection

1. Open **Cards** in the sidebar.
2. Find the card and click its **Settings** button.
3. Scroll to the **Tap Protection** section.
4. Switch **Tap Protection** to **ON**.
5. Click **Save**.

Once ON, the two sub-protections show up underneath. They come **enabled by default**.

## How to disable Tap Protection

1. Open **Cards → Settings** for the card.
2. Turn the **Tap Protection** master toggle **OFF**.
3. Click **Save**.

While OFF, every legitimate tap is counted. Duplicate and Dwell rules are ignored entirely.

## How to configure Duplicate Protection

Blocks the same phone from counting more than once within a chosen window.

1. With **Tap Protection** ON, look at the **Duplicate Protection** subsection.
2. Toggle it **ON** (default) or **OFF**.
3. When ON, set the **Window (seconds)** — the cool-down between two taps from the same device.
   * \`30 s\` (default) — general purpose.
   * \`60 s\` — restaurants with high-turnover counters where associate + customer may tap together.
   * \`15 s\` — high-volume kiosks where nearly every tap is a distinct customer.
4. Click **Save**.

## How to configure Dwell Protection

Requires the customer to spend a minimum time on your Landing Page or Review Shield before the tap counts.

1. With **Tap Protection** ON, look at the **Minimum Dwell Protection** subsection.
2. Toggle it **ON** (default) or **OFF**.
3. When ON, set the **Threshold (seconds)** — the minimum time.
   * \`10 s\` (default) — most businesses.
   * \`20 s\` — if you have a long branded intro video and want real engagement.
   * \`5 s\` — very short landing content, fast retail flow.
4. Click **Save**.

> On a **Direct Tap** card (no landing page), Dwell Protection is shown **disabled** with an amber notice. This is intentional — you cannot enable it there, because there is no dwell to measure. Switching the card to Landing Page mode automatically re-enables the toggle.

---

## How Direct Tap differs from Landing Page

| Aspect | Direct Tap | Landing Page |
|---|---|---|
| What the customer sees when they tap | Immediate redirect to Google Reviews | Your branded page first, then redirect |
| Duplicate Protection | Applies | Applies |
| Dwell Protection | Not Applicable | Applies |
| Anti-fraud rigor | Duplicate only | Duplicate + Dwell |
| Ideal for | Fast, frictionless conversion | Branding, incentives, multi-platform prompts |

## How Review Shield behaves

Review Shield is a special kind of landing page: the customer must complete a satisfaction gate before being routed to Google (satisfied) or your private feedback form (unsatisfied). From Tap Protection's point of view, Review Shield is treated **exactly like Landing Page**:

* Duplicate Protection applies.
* Dwell Protection applies.
* Both are configurable independently.

---

## How to interpret Health Monitor results

The **Tap Analytics Health Monitor** page (\`/admin/tap-health\`) mirrors what the runtime is enforcing. Open a card's *One-click Diagnostic* to see:

**Tap Protection: Enabled / Disabled**
* Enabled → at least one sub-protection may be running.
* Disabled → nothing is filtered.

**Duplicate Protection:**
* *Enabled · 30s window* — module runs, blocks repeat taps inside 30 s.
* *Disabled* — module is off (but Tap Protection master may still be on).
* *Disabled (Tap Protection off)* — the master switch is off.

**Minimum Dwell Protection:**
* *Enabled · 10s threshold* — module is running for this card.
* *Not Applicable* — the card is Direct Tap; there is no page to dwell on.
* *Disabled* — module has been turned off manually.

The **Recent Suppressions** list shows every recent rejected tap with its human-readable reason ("Duplicate Tap" or "Minimum Dwell Time Not Met"). If a card is complaining about missing taps, this is where you look first.

## How to troubleshoot rejected taps

**Symptom — "My taps are not counting"**

1. Open \`/admin/tap-health\` and enter the card's ID in the *One-click Diagnostic* box.
2. Read the **Verdict** badge at the top.

**Verdict: healthy** — Tap Protection is fine. The problem is probably not here. Check that:
* The customer is actually tapping the correct card.
* The card is not in \`disabled\` status.
* The redirect URL points where you expect.

**Verdict: silent** — the card recorded zero taps in 7 days. Investigate whether the card is being tapped at all (physical / logistical) or whether the redirect is broken.

**Verdict: silent-with-suppressions** — the card *is* being tapped, but every tap is getting rejected. Look at the *Recent Suppressions* list:
* Many **Duplicate Tap** entries → your Duplicate Window is too generous for your traffic; lower it, or turn Duplicate Protection off temporarily.
* Many **Minimum Dwell Time Not Met** → customers are leaving the page faster than your threshold; lower the threshold, or turn the landing page auto-redirect on so they don't have to wait.

**Verdict: write-failures** — the platform is failing to persist the taps to the database. Escalate to engineering with the timestamps and error types shown in the diagnostic.

---

## Frequently asked questions

**Can I disable one protection but keep the other?**
Yes. Duplicate and Dwell are fully independent toggles.

**If I turn Tap Protection OFF, will I lose historical data?**
No. The switch only affects future taps. Historical taps and suppression logs are untouched.

**Why is Dwell Protection greyed out on some cards?**
Because those cards are in **Direct Tap** mode with no landing page — there is no page to dwell on, so the setting has no meaning. Switching the card to Landing Page mode unlocks it.

**Does Tap Protection affect Google Reviews?**
No. Tap Protection only decides whether the platform *counts* a tap in analytics/incentives. The customer's redirect to Google Reviews happens either way.

**How do I test my new configuration?**
1. Tap the card yourself once.
2. Immediately open \`/admin/tap-health\` → Diagnose → your card_id.
3. You should see the tap in *Taps (24h)*. Tap again quickly — the second tap should either count (Duplicate off) or appear in *Recent Suppressions* as "Duplicate Tap" (Duplicate on).
`;

export default content;
